import React, { useCallback, useEffect, useState, useRef } from 'react';
import { XIcon, UserIcon, CalendarIcon, BookIcon, CheckIcon, SearchIcon, PlusIcon, RepeatIcon, LinkIcon } from 'lucide-react';
import { useTheme } from './providers/ThemeProvider';
import { toast } from 'react-hot-toast';
import { getTeacherStudents } from '../utils/teacherUtils';
import { addStudentToTeacher } from '../services/studentService';
import { createRecurringTasks } from '../services/taskService';
import { collection, query, where, orderBy, getDocs, limit, Timestamp, doc as firestoreDoc } from 'firebase/firestore';
import { db } from '../firebase';
interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddTask?: (taskData: any) => void;
  darkMode?: boolean;
  teacherId?: string;
  lessonId?: string;
  studentId?: string;
}
interface Student {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  instrument?: string;
  studentId?: string;
}
interface StudentLesson {
  id: string;
  startAt: string;
  instrument: string;
  title?: string;
}
export const AddTaskModal: React.FC<AddTaskModalProps> = ({
  isOpen,
  onClose,
  onAddTask,
  darkMode: darkModeProp,
  teacherId = '',
  lessonId,
  studentId: preselectedStudentId
}) => {
  // Get theme from context
  const {
    theme
  } = useTheme();
  const isDark = darkModeProp !== undefined ? darkModeProp : theme === 'dark';
  // Form state
  const [title, setTitle] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [reference, setReference] = useState('');
  // Lesson linking state
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [studentLessons, setStudentLessons] = useState<StudentLesson[]>([]);
  const [isLoadingLessons, setIsLoadingLessons] = useState(false);
  // Recurring state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState<number[]>([]);
  const [recurringEndDate, setRecurringEndDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 28); // Default 4 weeks
    return date.toISOString().split('T')[0];
  });
  // Student picker state
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [isAddingNewStudent, setIsAddingNewStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const studentDropdownRef = useRef<HTMLDivElement>(null);
  const studentSearchInputRef = useRef<HTMLInputElement>(null);
  // Validation state
  const [errors, setErrors] = useState<{
    title?: string;
    student?: string;
    dueDate?: string;
    recurring?: string;
  }>({});
  // Focus management
  const titleInputRef = useRef<HTMLInputElement>(null);
  // Add drag state - OPTIMIZED: Use refs for drag offset to avoid re-renders
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({
    x: 0,
    y: 0
  });
  const rafIdRef = useRef<number | null>(null);
  const [dragStartPos, setDragStartPos] = useState({
    x: 0,
    y: 0
  });
  const [modalPosition, setModalPosition] = useState({
    top: 0,
    left: 0
  });
  const modalRef = useRef<HTMLDivElement>(null);
  // Load students from Firestore
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  useEffect(() => {
    const fetchStudents = async () => {
      if (isOpen && teacherId) {
        setIsLoadingStudents(true);
        try {
          const fetchedStudents = await getTeacherStudents(teacherId);
          // Filter for active students only
          const activeStudents = fetchedStudents.filter((student: any) => !student.status || student.status === 'active' || student.status === 'trial' || student.status === 'pending');
          setStudents(activeStudents);
        } catch (error) {
          console.error('Error loading students:', error);
          toast.error('Failed to load students');
        } finally {
          setIsLoadingStudents(false);
        }
      }
    };
    fetchStudents();
  }, [isOpen, teacherId]);
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setSelectedStudent(preselectedStudentId || '');
      setDueDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      setReference('');
      setSelectedLessonId(lessonId || '');
      setStudentLessons([]);
      setErrors({});
      setStudentSearch('');
      setIsAddingNewStudent(false);
      setIsRecurring(false);
      setRecurringDays([]);
      const date = new Date();
      date.setDate(date.getDate() + 28);
      setRecurringEndDate(date.toISOString().split('T')[0]);
      // Focus the title input when modal opens
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, preselectedStudentId, lessonId]);
  // Close student dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(event.target as Node)) {
        setIsStudentDropdownOpen(false);
        setIsAddingNewStudent(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Focus search input when student dropdown opens
  useEffect(() => {
    if (isStudentDropdownOpen && studentSearchInputRef.current) {
      setTimeout(() => {
        studentSearchInputRef.current?.focus();
      }, 50);
    }
  }, [isStudentDropdownOpen]);
  // Initialize modal position to center on mount
  useEffect(() => {
    if (isOpen && modalRef.current && window.innerWidth >= 768) {
      const modalWidth = 512; // max-w-lg = 32rem = 512px
      const modalHeight = modalRef.current.offsetHeight || 600;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const left = (viewportWidth - modalWidth) / 2;
      const top = (viewportHeight - modalHeight) / 2;
      setModalPosition({
        top,
        left
      });
      // Reset drag offset on every open
      dragOffsetRef.current = {
        x: 0,
        y: 0
      };
    }
  }, [isOpen]);
  // Add drag handlers - OPTIMIZED: Use transform + RAF like CreateLessonModal
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // Disable drag on mobile
    if (window.innerWidth < 768) return;
    if (!modalRef.current) return;
    // Don't start drag if clicking on close button
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX,
      y: e.clientY
    });
  }, []);
  const handleDrag = useCallback((e: MouseEvent) => {
    if (!modalRef.current) return;
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    dragOffsetRef.current = {
      x: deltaX,
      y: deltaY
    };
    // Cancel previous RAF if still pending
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    // Schedule DOM update on next frame
    rafIdRef.current = requestAnimationFrame(() => {
      if (modalRef.current) {
        const finalTop = modalPosition.top + dragOffsetRef.current.y;
        const finalLeft = modalPosition.left + dragOffsetRef.current.x;
        modalRef.current.style.transform = `translate(${finalLeft - modalPosition.left}px, ${finalTop - modalPosition.top}px)`;
      }
    });
  }, [dragStartPos, modalPosition]);
  const handleDragEnd = useCallback(() => {
    // Cancel any pending RAF
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    // Calculate new position with bounds checking
    const modalWidth = modalRef.current?.offsetWidth || 512;
    const modalHeight = modalRef.current?.offsetHeight || 600;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let newLeft = modalPosition.left + dragOffsetRef.current.x;
    let newTop = modalPosition.top + dragOffsetRef.current.y;
    // Constrain to viewport bounds
    newLeft = Math.max(0, Math.min(newLeft, viewportWidth - modalWidth));
    newTop = Math.max(0, Math.min(newTop, viewportHeight - modalHeight));
    // CRITICAL: Apply final position to DOM synchronously BEFORE clearing transform
    // This prevents any frame where the element appears at old position
    if (modalRef.current) {
      // Set final position directly on element (synchronous)
      modalRef.current.style.top = `${newTop}px`;
      modalRef.current.style.left = `${newLeft}px`;
      // Now safe to clear transform - element is already at final position
      modalRef.current.style.transform = '';
    }
    // Update React state to match DOM (keeps state in sync)
    setModalPosition({
      top: newTop,
      left: newLeft
    });
    setIsDragging(false);
    dragOffsetRef.current = {
      x: 0,
      y: 0
    };
  }, [modalPosition]);
  // Add global mouse event listeners for dragging - OPTIMIZED
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
        // Cleanup RAF on unmount
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);
  // Prevent text selection while dragging
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'move';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);
  // Lock background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Fetch lessons for selected student
  useEffect(() => {
    const fetchStudentLessons = async () => {
      // Don't fetch if no student selected, no teacherId, or lessonId already provided via prop
      if (!selectedStudent || !teacherId || lessonId) {
        setStudentLessons([]);
        return;
      }
      setIsLoadingLessons(true);
      try {
        const calendarRef = collection(db, 'teachers', teacherId, 'calendar');
        // ===== DIAGNOSTIC: Query WITHOUT date filter to isolate the issue =====
        const studentData = students.find(s => s.id === selectedStudent);
        const altStudentId = studentData?.studentId;
        console.log('[LINK LESSON DIAG] selectedStudent (doc.id):', selectedStudent);
        console.log('[LINK LESSON DIAG] student data.studentId field:', altStudentId);
        console.log('[LINK LESSON DIAG] teacherId:', teacherId);
        // Query 1: No date filter, using doc.id
        const diagQuery1 = query(calendarRef, where('studentId', '==', selectedStudent), limit(5));
        const diagSnap1 = await getDocs(diagQuery1);
        console.log('[LINK LESSON DIAG] Lessons found with doc.id (no date filter):', diagSnap1.size);
        diagSnap1.docs.forEach(d => {
          const data = d.data();
          console.log('[LINK LESSON DIAG]   lesson:', d.id, 'studentId:', data.studentId, 'startAt:', data.startAt?.toDate?.()?.toISOString?.() || data.startAt);
        });
        // Query 2: No date filter, using altStudentId (if different)
        if (altStudentId && altStudentId !== selectedStudent) {
          const diagQuery2 = query(calendarRef, where('studentId', '==', altStudentId), limit(5));
          const diagSnap2 = await getDocs(diagQuery2);
          console.log('[LINK LESSON DIAG] Lessons found with data.studentId (no date filter):', diagSnap2.size);
          diagSnap2.docs.forEach(d => {
            const data = d.data();
            console.log('[LINK LESSON DIAG]   lesson:', d.id, 'studentId:', data.studentId, 'startAt:', data.startAt?.toDate?.()?.toISOString?.() || data.startAt);
          });
        }
        // ===== END DIAGNOSTIC =====
        const now = new Date();
        // 7 days ago
        const pastCutoff = new Date(now);
        pastCutoff.setDate(pastCutoff.getDate() - 7);
        pastCutoff.setHours(0, 0, 0, 0);
        // 30 days from now
        const futureCutoff = new Date(now);
        futureCutoff.setDate(futureCutoff.getDate() + 30);
        futureCutoff.setHours(23, 59, 59, 999);
        console.log('[LINK LESSON DIAG] Date filter range:', pastCutoff.toISOString(), 'to', futureCutoff.toISOString());
        // Helper to run a lesson query for a given studentId value
        const queryLessonsById = async (studentIdValue: string): Promise<StudentLesson[]> => {
          const lessonsQuery = query(calendarRef, where('studentId', '==', studentIdValue), where('startAt', '>=', Timestamp.fromDate(pastCutoff)), where('startAt', '<=', Timestamp.fromDate(futureCutoff)), orderBy('startAt', 'desc'), limit(20));
          const snapshot = await getDocs(lessonsQuery);
          console.log('[LINK LESSON DIAG] Filtered query for', studentIdValue, '→', snapshot.size, 'results');
          return snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            const startAt = data.startAt?.toDate?.() ? data.startAt.toDate().toISOString() : data.startAt;
            return {
              id: docSnap.id,
              startAt,
              instrument: data.instrument || '',
              title: data.title || ''
            };
          });
        };
        // First attempt: query using doc.id (selectedStudent)
        let lessons = await queryLessonsById(selectedStudent);
        // Fallback: if no results, check if the student has a different data.studentId field
        if (lessons.length === 0) {
          if (altStudentId && altStudentId !== selectedStudent) {
            console.log('[LINK LESSON] No lessons found with doc.id, retrying with data.studentId:', altStudentId);
            lessons = await queryLessonsById(altStudentId);
          }
        }
        setStudentLessons(lessons);
      } catch (error) {
        console.error('[LINK LESSON] Error fetching student lessons:', error);
        setStudentLessons([]);
      } finally {
        setIsLoadingLessons(false);
      }
    };
    fetchStudentLessons();
  }, [selectedStudent, teacherId, lessonId, students]);
  // Reset lesson selection when student changes
  useEffect(() => {
    if (!lessonId) {
      setSelectedLessonId('');
    }
  }, [selectedStudent, lessonId]);
  const calculateRecurringCount = () => {
    if (!isRecurring || recurringDays.length === 0 || !recurringEndDate) return 0;
    let count = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(recurringEndDate + 'T23:59:59');
    const cursor = new Date(today);
    while (cursor <= end) {
      if (recurringDays.includes(cursor.getDay())) {
        count++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return count;
  };
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    const newErrors: {
      title?: string;
      student?: string;
      dueDate?: string;
      recurring?: string;
    } = {};
    if (!title.trim()) {
      newErrors.title = 'Task title is required';
    }
    if (!selectedStudent) {
      newErrors.student = 'Please select a student';
    }
    if (!isRecurring && !dueDate) {
      newErrors.dueDate = 'Due date is required';
    }
    if (isRecurring) {
      if (recurringDays.length === 0) {
        newErrors.recurring = 'Please select at least one day';
      }
      if (!recurringEndDate) {
        newErrors.recurring = 'End date is required';
      }
      if (calculateRecurringCount() === 0) {
        newErrors.recurring = 'No tasks will be created with these settings';
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (isRecurring && teacherId) {
      try {
        const {
          count
        } = await createRecurringTasks(teacherId, selectedStudent, {
          title,
          notes,
          reference,
          studentName: students.find(s => s.id === selectedStudent)?.name || ''
        }, {
          frequency: 'weekly',
          days: recurringDays,
          endDate: recurringEndDate
        });
        toast.success(`Created ${count} recurring tasks!`);
        onClose();
        // Force a reload to refresh the task list since parent doesn't handle batch creation natively yet
        setTimeout(() => window.location.reload(), 500);
      } catch (error) {
        console.error('Error creating recurring tasks:', error);
        toast.error('Failed to create recurring tasks');
      }
    } else {
      // Create single task data
      const effectiveLessonId = lessonId || selectedLessonId || undefined;
      const taskData = {
        id: 'task_' + Date.now().toString(),
        title,
        name: title,
        studentId: selectedStudent,
        studentName: students.find(s => s.id === selectedStudent)?.name || '',
        dueDate,
        deadline: dueDate,
        notes,
        reference,
        status: 'pending',
        teacherId: teacherId,
        createdAt: new Date().toISOString(),
        ...(effectiveLessonId && {
          lessonId: effectiveLessonId
        })
      };
      // Call onAddTask callback - parent will handle persistence
      if (onAddTask) {
        onAddTask(taskData);
      }
      // Show success message
      toast.success('Task created successfully!');
      // Close modal
      onClose();
    }
  };
  // Check if form is valid
  const isFormValid = () => {
    const baseValid = title.trim() !== '' && selectedStudent !== '';
    if (isRecurring) {
      return baseValid && recurringDays.length > 0 && recurringEndDate !== '' && calculateRecurringCount() > 0;
    }
    return baseValid && dueDate !== '';
  };
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);
  // Filter students based on search
  const filteredStudents = students.filter(student => student.name.toLowerCase().includes(studentSearch.toLowerCase()) || student.email && student.email.toLowerCase().includes(studentSearch.toLowerCase()));
  // Handle add new student
  const handleAddStudent = async () => {
    if (!newStudent.name.trim()) return;
    if (!teacherId) {
      toast.error('Cannot add student: not logged in');
      return;
    }
    try {
      // Show loading state
      setIsLoadingStudents(true);
      // Prepare student data for Firestore
      const nameParts = newStudent.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const studentData = {
        studentId: `manual-${Date.now()}`,
        name: newStudent.name.trim(),
        firstName: firstName,
        lastName: lastName,
        email: newStudent.email.trim() || '',
        phone: newStudent.phone.trim() || '',
        instrument: '',
        status: 'active' as const,
        notes: ''
      };
      console.log('[ADD STUDENT] Saving to Firestore:', studentData);
      // ✅ CRITICAL FIX: Save to Firestore
      const studentDocId = await addStudentToTeacher(teacherId, studentData);
      console.log('[ADD STUDENT] Saved successfully with ID:', studentDocId);
      // ✅ Refresh student list from Firestore
      const fetchedStudents = await getTeacherStudents(teacherId);
      const activeStudents = fetchedStudents.filter((student: any) => !student.status || student.status === 'active' || student.status === 'trial' || student.status === 'pending');
      setStudents(activeStudents);
      console.log('[ADD STUDENT] Student list refreshed, total:', activeStudents.length);
      // ✅ Find and select the newly created student
      const newlyCreatedStudent = activeStudents.find(s => s.id === studentDocId);
      if (newlyCreatedStudent) {
        setSelectedStudent(newlyCreatedStudent.id);
        console.log('[ADD STUDENT] Auto-selected new student:', newlyCreatedStudent.name);
      } else {
        console.warn('[ADD STUDENT] Could not find newly created student in list');
      }
      // Reset form
      setNewStudent({
        name: '',
        email: '',
        phone: ''
      });
      setIsAddingNewStudent(false);
      setIsStudentDropdownOpen(false);
      // Clear validation errors
      if (errors.student) {
        setErrors(prev => {
          const newErrors = {
            ...prev
          };
          delete newErrors.student;
          return newErrors;
        });
      }
      // ✅ Show success message ONLY after Firestore confirms
      toast.success('Student added successfully!');
    } catch (error) {
      console.error('[ADD STUDENT] Error saving to Firestore:', error);
      // ✅ Show clear error message
      const errorMessage = error.message || 'Failed to add student. Please try again.';
      toast.error(errorMessage);
      // Do NOT add student to local state on error
      // Keep the form open so user can retry
    } finally {
      setIsLoadingStudents(false);
    }
  };
  // Handle student selection
  const handleSelectStudent = (studentId: string) => {
    setSelectedStudent(studentId);
    setIsStudentDropdownOpen(false);
    if (errors.student) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors.student;
        return newErrors;
      });
    }
  };
  // Clear selected student
  const handleClearStudent = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedStudent('');
  };
  // Get selected student
  const selectedStudentData = students.find(s => s.id === selectedStudent);
  if (!isOpen) return null;
  // Calculate final position
  const finalTop = modalPosition.top + dragOffsetRef.current.y;
  const finalLeft = modalPosition.left + dragOffsetRef.current.x;
  return <div className="fixed inset-0 z-50 flex flex-col md:block md:overflow-y-auto" data-id="element-408">
      <div className="flex-1 flex items-stretch justify-center px-4 py-4 text-center md:flex-none md:flex md:min-h-screen md:items-center md:justify-center" data-id="element-409">
        {/* Enhanced background overlay with blur */}
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity z-10" onClick={onClose} aria-hidden="true" data-id="element-410"></div>

        {/* Modal panel */}
        <div ref={modalRef} className={`relative z-20 ${isDark ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50 shadow-2xl' : 'bg-white border border-gray-200 shadow-xl'} rounded-xl px-4 pt-5 pb-4 ${isDragging ? 'transition-none' : ''} sm:p-6 w-full max-w-lg max-h-[calc(100dvh-2rem)] md:max-h-[90vh] flex flex-col overflow-hidden box-border ${window.innerWidth >= 768 ? 'md:fixed' : ''} ${isDragging ? 'cursor-move' : ''}`} style={{
        ...(window.innerWidth >= 768 ? {
          top: `${modalPosition.top}px`,
          left: `${modalPosition.left}px`
        } : {}),
        userSelect: isDragging ? 'none' : 'auto',
        touchAction: 'pan-y',
        ...(isDark ? {
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
        } : {})
      }} onClick={e => e.stopPropagation()} data-id="element-411">
          {/* Header - fixed at top */}
          <div className="flex items-center justify-between mb-6 md:cursor-move flex-shrink-0" onMouseDown={handleDragStart} title={window.innerWidth >= 768 ? 'Drag to move' : undefined} data-id="element-412">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-413">
              Create New Task
            </h3>
            <button onClick={onClose} className={`${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-700/50' : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'} focus:outline-none rounded-lg p-1.5 transition-all`} aria-label="Close" data-id="element-414">
              <XIcon className="h-5 w-5" data-id="element-415" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col" data-id="element-416">
            {/* Scrollable content area */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-5 text-left pr-2 md:pr-4" data-id="element-417">
              {/* Task Title */}
              <div data-id="element-418">
                <label htmlFor="title" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-419">
                  Task Title*
                </label>
                <input type="text" id="title" ref={titleInputRef} value={title} onChange={e => setTitle(e.target.value)} placeholder="Add task title" className={`w-full rounded-lg border outline-none ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${errors.title ? 'border-red-500 focus:border-red-500' : isDark ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-gray-900 focus:ring-1 focus:ring-gray-900/20'} shadow-sm transition-all px-4 py-2.5`} required data-id="element-420" />
                {errors.title && <p className="mt-1.5 text-sm text-red-400" data-id="element-421">{errors.title}</p>}
              </div>

              {/* Student Selection */}
              <div data-id="element-422">
                <label htmlFor="student" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-423">
                  Assign to Student*
                </label>
                <div className="relative" ref={studentDropdownRef} data-id="element-424">
                  <div className={`flex items-center justify-between w-full rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} ${errors.student ? 'border-red-500' : isDark ? 'focus-within:border-[#10B981] focus-within:ring-1 focus-within:ring-[#10B981]/20' : 'focus-within:border-gray-900 focus-within:ring-1 focus-within:ring-gray-900/20'} px-4 py-2.5 cursor-pointer shadow-sm transition-all`} onClick={() => setIsStudentDropdownOpen(!isStudentDropdownOpen)} data-id="element-425">
                    <div className="flex items-center flex-grow" data-id="element-426">
                      <UserIcon className="h-4 w-4 mr-2.5 flex-shrink-0" style={{
                      color: '#3B82F6'
                    }} data-id="element-427" />
                      {!isStudentDropdownOpen ? <span className={selectedStudentData ? 'text-sm' : `text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-428">
                          {isLoadingStudents ? 'Loading students...' : selectedStudentData ? selectedStudentData.name : 'Select a student'}
                        </span> : <input ref={studentSearchInputRef} type="text" value={studentSearch} onChange={e => setStudentSearch(e.target.value)} onClick={e => e.stopPropagation()} placeholder="Search students..." className={`w-full border-none outline-none ${isDark ? 'bg-transparent text-white' : 'bg-transparent text-gray-900'} placeholder:text-gray-500 focus:ring-0 focus:outline-none`} data-id="element-429" />}
                    </div>
                    {selectedStudent && !isStudentDropdownOpen && <button type="button" onClick={handleClearStudent} className={`ml-2 p-1 rounded-lg ${isDark ? 'hover:bg-navy-700/50' : 'hover:bg-gray-100'} transition-colors`} data-id="element-430">
                        <XIcon className="h-4 w-4 text-gray-400" data-id="element-431" />
                      </button>}
                    {isStudentDropdownOpen && <SearchIcon className="h-4 w-4 text-gray-400 ml-2" data-id="element-432" />}
                  </div>

                  {/* Dropdown content */}
                  {isStudentDropdownOpen && <div className={`absolute z-10 w-full mt-2 rounded-lg shadow-xl ${isDark ? 'bg-navy-800 border border-navy-700/50' : 'bg-white border border-gray-200'}`} style={isDark ? {
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
                } : {}} data-id="element-433">
                      {!isAddingNewStudent && <ul className={`max-h-60 overflow-auto py-1 text-sm ${isDark ? 'text-white' : 'text-gray-700'}`} data-id="element-434">
                          {isLoadingStudents ? <li className="px-4 py-2.5 text-center text-gray-500" data-id="element-435">
                              Loading students...
                            </li> : filteredStudents.length > 0 ? filteredStudents.map(student => <li key={student.id} className={`px-4 py-2.5 cursor-pointer flex items-center justify-between ${student.id === selectedStudent ? isDark ? 'bg-navy-700/50' : 'bg-gray-100' : isDark ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} transition-colors`} onClick={() => handleSelectStudent(student.id)} data-id="element-436">
                                <span className="flex-grow" data-id="element-437">
                                  {student.name}
                                </span>
                                {student.email && <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} ml-2`} data-id="element-438">
                                    {student.email}
                                  </span>}
                              </li>) : <li className="px-4 py-2.5 text-center text-gray-500" data-id="element-439">
                              No students found
                            </li>}
                          <li className={`px-4 py-2.5 cursor-pointer flex items-center ${isDark ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} text-[#10B981] border-t ${isDark ? 'border-navy-700/50' : 'border-gray-100'} font-medium transition-colors`} onClick={() => setIsAddingNewStudent(true)} data-id="element-440">
                            <PlusIcon className="h-4 w-4 mr-2" data-id="element-441" />
                            <span data-id="element-442">Add new student</span>
                          </li>
                        </ul>}

                      {isAddingNewStudent && <div className={`p-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-443">
                          <h4 className="font-medium mb-3 text-sm" data-id="element-444">
                            Add New Student
                          </h4>
                          <div className="space-y-3" data-id="element-445">
                            <input type="text" value={newStudent.name} onChange={e => setNewStudent({
                        ...newStudent,
                        name: e.target.value
                      })} placeholder="Student name *" className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 transition-all`} autoFocus data-id="element-446" />
                            <input type="email" value={newStudent.email} onChange={e => setNewStudent({
                        ...newStudent,
                        email: e.target.value
                      })} placeholder="Email (optional)" className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 transition-all`} data-id="element-447" />
                            <input type="tel" value={newStudent.phone} onChange={e => setNewStudent({
                        ...newStudent,
                        phone: e.target.value
                      })} placeholder="Phone (optional)" className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 transition-all`} data-id="element-448" />
                            <div className="flex justify-end space-x-2 pt-1" data-id="element-449">
                              <button type="button" onClick={() => setIsAddingNewStudent(false)} className={`px-4 py-2 text-sm rounded-lg font-medium ${isDark ? 'bg-navy-700/50 text-white hover:bg-navy-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} transition-colors`} data-id="element-450">
                                Cancel
                              </button>
                              <button type="button" onClick={handleAddStudent} disabled={!newStudent.name.trim()} className={`px-4 py-2 text-sm rounded-lg font-medium border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 transition-colors ${!newStudent.name.trim() ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-451">
                                Add Student
                              </button>
                            </div>
                          </div>
                        </div>}
                    </div>}
                </div>
                {errors.student && <p className="mt-1.5 text-sm text-red-400" data-id="element-452">
                    {errors.student}
                  </p>}
              </div>

              {/* Link to Lesson (Optional) — only shown when student selected, lessons exist, not recurring, and no lessonId prop */}
              {selectedStudent && !isRecurring && !lessonId && <div data-id="element-453">
                  <label htmlFor="linkLesson" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-454">
                    Link to Lesson (Optional)
                  </label>
                  <div className="relative" data-id="element-455">
                    <select id="linkLesson" value={selectedLessonId} onChange={e => setSelectedLessonId(e.target.value)} className={`w-full rounded-lg border outline-none appearance-none ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} ${isDark ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-gray-900 focus:ring-1 focus:ring-gray-900/20'} shadow-sm transition-all pl-11 pr-4 py-2.5 text-sm`} data-id="element-456">
                      <option value="" data-id="element-457">
                        {isLoadingLessons ? 'Loading lessons...' : studentLessons.length === 0 ? 'No recent lessons' : 'Select a lesson'}
                      </option>
                      {studentLessons.map(lesson => {
                    const lessonDate = new Date(lesson.startAt);
                    const dateStr = lessonDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    });
                    const label = lesson.instrument ? `${dateStr} — ${lesson.instrument}` : dateStr;
                    return <option key={lesson.id} value={lesson.id} data-id="element-458">
                            {label}
                          </option>;
                  })}
                    </select>
                    <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-459">
                      <LinkIcon className="h-4 w-4" style={{
                    color: '#3B82F6'
                  }} data-id="element-460" />
                    </div>
                  </div>
                </div>}

              {!isRecurring && <div data-id="element-461">
                  <label htmlFor="dueDate" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-462">
                    Due Date*
                  </label>
                  <div className="relative" data-id="element-463">
                    <input type="date" id="dueDate" value={dueDate} onChange={e => setDueDate(e.target.value)} className={`w-full max-w-full min-w-0 rounded-lg border outline-none ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} ${errors.dueDate ? 'border-red-500 focus:border-red-500' : isDark ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-gray-900 focus:ring-1 focus:ring-gray-900/20'} shadow-sm transition-all pl-11 pr-4 py-2.5`} required={!isRecurring} data-id="element-464" />
                    <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-465">
                      <CalendarIcon className="h-4 w-4" style={{
                    color: '#3B82F6'
                  }} data-id="element-466" />
                    </div>
                  </div>
                  {errors.dueDate && <p className="mt-1.5 text-sm text-red-400" data-id="element-467">
                      {errors.dueDate}
                    </p>}
                </div>}

              {/* Recurring Toggle */}
              <div className="pt-2" data-id="element-468">
                <div className="flex items-center justify-between" data-id="element-469">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-470">
                    Repeat this task
                  </label>
                  <button type="button" onClick={() => setIsRecurring(!isRecurring)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isRecurring ? isDark ? 'bg-[#10B981]' : 'bg-[#10B981]' : isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-471">
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isRecurring ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-472" />
                  </button>
                </div>

                {isRecurring && <div className={`mt-4 p-4 rounded-lg border ${isDark ? 'bg-navy-900/30 border-navy-700/50' : 'bg-gray-50 border-gray-200'} space-y-4`} data-id="element-473">
                    <div data-id="element-474">
                      <label className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-2`} data-id="element-475">
                        Repeat on days
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-476">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => <button key={day} type="button" onClick={() => {
                      if (recurringDays.includes(index)) {
                        setRecurringDays(recurringDays.filter(d => d !== index));
                      } else {
                        setRecurringDays([...recurringDays, index]);
                      }
                    }} className={`w-10 h-10 rounded-full text-sm font-medium flex items-center justify-center transition-colors ${recurringDays.includes(index) ? 'bg-[#10B981] text-white' : isDark ? 'bg-navy-800 text-gray-300 hover:bg-navy-700' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`} data-id="element-477">
                              {day[0]}
                            </button>)}
                      </div>
                    </div>

                    <div data-id="element-478">
                      <label className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-2`} data-id="element-479">
                        End date
                      </label>
                      <input type="date" value={recurringEndDate} onChange={e => {
                    if (e.target.value) setRecurringEndDate(e.target.value);
                  }} min={new Date().toISOString().split('T')[0]} className={`w-full rounded-lg border outline-none ${isDark ? 'bg-navy-800 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} px-3 py-2 text-sm ${isDark ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-gray-900 focus:ring-1 focus:ring-gray-900/20'}`} data-id="element-480" />
                    </div>

                    <div className={`text-sm font-medium flex items-center ${isDark ? 'text-[#10B981]' : 'text-[#10B981]'}`} data-id="element-481">
                      <RepeatIcon className="h-4 w-4 mr-1.5" data-id="element-482" />
                      Will create {calculateRecurringCount()} tasks through{' '}
                      {new Date(recurringEndDate).toLocaleDateString()}
                    </div>
                    {errors.recurring && <p className="mt-1 text-sm text-red-400" data-id="element-483">
                        {errors.recurring}
                      </p>}
                  </div>}
              </div>

              {/* Reference */}
              <div data-id="element-484">
                <label htmlFor="reference" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-485">
                  Reference (Optional)
                </label>
                <input type="text" id="reference" value={reference} onChange={e => setReference(e.target.value)} placeholder="e.g., Page 42, Exercise 3" className={`w-full rounded-lg border outline-none ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${isDark ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-gray-900 focus:ring-1 focus:ring-gray-900/20'} shadow-sm transition-all px-4 py-2.5`} data-id="element-486" />
              </div>

              {/* Notes */}
              <div data-id="element-487">
                <label htmlFor="notes" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-488">
                  Notes (Optional)
                </label>
                <div className="relative" data-id="element-489">
                  <textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3} className={`w-full rounded-lg border outline-none ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${isDark ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-gray-900 focus:ring-1 focus:ring-gray-900/20'} shadow-sm transition-all pl-11 pr-4 py-2.5`} placeholder="Add any notes about this task..." data-id="element-490"></textarea>
                  <div className="absolute top-3.5 left-3.5 pointer-events-none" data-id="element-491">
                    <BookIcon className="h-4 w-4" style={{
                    color: '#9CA3AF'
                  }} data-id="element-492" />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons - sticky footer */}
            <div className="flex justify-end space-x-3 mt-4 pt-4 border-t border-navy-700/30 flex-shrink-0" data-id="element-493">
              <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-navy-700/50 text-white hover:bg-navy-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} focus:outline-none transition-colors`} data-id="element-494">
                Cancel
              </button>
              <button type="submit" className={`px-4 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none flex items-center transition-colors ${!isFormValid() ? 'opacity-50 cursor-not-allowed border-gray-600 text-gray-600' : isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} disabled={!isFormValid()} data-id="element-495">
                <CheckIcon className="h-4 w-4 mr-1" data-id="element-496" />
                Save Task
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Add styles to fix date input visibility */}
      <style jsx global data-id="element-497">{`
        input[type='date']::-webkit-calendar-picker-indicator {
          opacity: 0;
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          cursor: pointer;
        }
        input[type='date'] {
          position: relative;
          overflow: hidden;
          color: ${isDark ? 'white' : '#1f2937'} !important;
        }

        /* Mobile-only: Prevent date input overflow */
        @media (max-width: 767px) {
          input[type='date'] {
            min-width: 0 !important;
            max-width: 100% !important;
            -webkit-appearance: none !important;
            -moz-appearance: textfield !important;
            appearance: none !important;
          }
        }

        input:focus,
        textarea:focus {
          caret-color: ${isDark ? 'white' : '#1f2937'} !important;
        }
      `}</style>
    </div>;
};